import React, {useEffect, useState} from 'react';
import {api} from '../../api';

export default function EventModal({ event, defaultStart, defaultEnd, defaultAllDay, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState({ title: '', description: '', startTime: '', endTime: '', allDay: false, sourceType: '', sourceId: '', color: 'blue' });
  const [jobs, setJobs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api('/jobs').then(r => r.ok ? r.json() : []).then(setJobs);
  }, []);

  useEffect(() => {
    if (event) {
      setForm({
        title: event.title || '',
        description: event.description || '',
        startTime: toLocalInput(event.startTime),
        endTime: toLocalInput(event.endTime),
        allDay: event.allDay || false,
        sourceType: event.sourceType || '',
        sourceId: event.sourceId || '',
        color: event.color || 'blue',
      });
    } else {
      setForm(f => ({
        ...f,
        startTime: defaultStart || '',
        endTime: defaultEnd || '',
        allDay: defaultAllDay || false,
      }));
    }
  }, [event, defaultStart, defaultEnd, defaultAllDay]);

  const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      title: form.title,
      description: form.description || null,
      startTime: new Date(form.startTime).toISOString(),
      endTime: new Date(form.endTime).toISOString(),
      allDay: form.allDay,
      sourceType: form.sourceType || null,
      sourceId: form.sourceId || null,
      color: form.color,
    };
    const url = event ? `/calendar/events/${event.id}` : '/calendar/events';
    const method = event ? 'PUT' : 'POST';
    const res = await api(url, { method, body: JSON.stringify(body) });
    setSaving(false);
    if (res.ok) { const saved = await res.json(); onSaved(saved); }
  };

  const handleDelete = async () => {
    await api(`/calendar/events/${event.id}`, { method: 'DELETE' });
    onDeleted(event.id);
  };

  const handleJobLink = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    setForm(f => ({
      ...f,
      sourceType: jobId ? 'JOB' : '',
      sourceId: jobId || '',
      title: jobId && job ? `${job.jobNumber} – ${job.title}` : f.title,
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">{event ? 'Edit Event' : 'New Event'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Link to Job</label>
            <select value={form.sourceId} onChange={e => handleJobLink(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none">
              <option value="">None</option>
              {jobs.map(j => <option key={j.id} value={j.id}>{j.jobNumber} – {j.title}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input type="text" required value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.allDay} onChange={e => setForm(f => ({...f, allDay: e.target.checked}))} />
            All day
          </label>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <div className="flex gap-2 flex-wrap">
              {[{key:'blue',bg:'bg-blue-500'},{key:'teal',bg:'bg-teal-500'},{key:'red',bg:'bg-red-500'},{key:'orange',bg:'bg-orange-500'},{key:'yellow',bg:'bg-yellow-500'},{key:'green',bg:'bg-green-500'},{key:'purple',bg:'bg-purple-500'},{key:'pink',bg:'bg-pink-500'},{key:'gray',bg:'bg-gray-500'}].map(c => (
                <button key={c.key} type="button" onClick={() => setForm(f => ({...f, color: c.key}))}
                  className={`w-7 h-7 rounded-full ${c.bg} ${form.color === c.key ? 'ring-2 ring-offset-2 ring-gray-900' : 'hover:scale-110'} transition-transform`} />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start *</label>
              <input type={form.allDay ? 'date' : 'datetime-local'} required value={form.allDay ? form.startTime?.slice(0, 10) : form.startTime}
                onChange={e => setForm(f => ({...f, startTime: form.allDay ? e.target.value + 'T00:00' : e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End *</label>
              <input type={form.allDay ? 'date' : 'datetime-local'} required value={form.allDay ? form.endTime?.slice(0, 10) : form.endTime}
                onChange={e => setForm(f => ({...f, endTime: form.allDay ? e.target.value + 'T23:59' : e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
          </div>
          <div className="flex justify-between pt-2">
            <div>
              {event && <button type="button" onClick={handleDelete} className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm">Delete</button>}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
